import type React from 'react';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Icon from '~/components2/Icon';
import store from '../store';
import { useShouldOpen } from './useShouldOpen';

interface ToggleProps {
  label: string;
  size?: 'base' | 'small';
  featureCount?: React.ReactNode;
  layerIds?: string[];
  defaultOpen?: boolean;
  children?: React.ReactNode;
  agencyIds?: string[];
}

export default function Toggle({
  label,
  size = 'base',
  featureCount,
  layerIds,
  defaultOpen = false,
  children,
  agencyIds,
}: ToggleProps) {
  const { selectedFeature } = useSnapshot(store);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useShouldOpen({ layerIds, setIsOpen });

  const handleToggle = () => {
    const open = !isOpen;
    setIsOpen(open);

    // Only track opens (mirrors the section/subsection toggle pattern); closing isn't tracked.
    if (open && store.project) {
      capturePosthogEvent(POSTHOG_EVENTS.intelligenceSubsectionExpanded, {
        project_id: store.project.id,
        organization_id: store.project.organization,
        subsection_name: label,
        ...(typeof featureCount === 'number' && { feature_count: featureCount }),
      });
    }
  };

  // this handles opening the parent toggle section, the useIsOpen will handle the subsections.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (selectedFeature) {
      const cgId = selectedFeature.properties?.civilgrid_id;

      if (cgId && agencyIds?.includes(cgId?.trim()?.toString())) {
        setIsOpen(true);
      }
    }
  }, [selectedFeature]);

  if (size === 'small') {
    return (
      <div className="mx-2 flex flex-col mt-2">
        <div
          onClick={handleToggle}
          className="flex w-full cursor-pointer items-center justify-between rounded py-1 px-2 hover:bg-grey-20"
        >
          <div className="text-sm font-medium">
            {label} {(featureCount || featureCount === 0) && <span className="text-xs">{`(${featureCount})`}</span>}
          </div>
          <div>
            <Icon icon={isOpen ? 'chevronUp' : 'chevronDown'} />
          </div>
        </div>
        {isOpen && <>{children}</>}
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded border">
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between rounded py-1 px-2 hover:bg-grey-20"
      >
        <div className="text-base font-medium">
          {label} {(featureCount || featureCount === 0) && <span className="text-sm">{`(${featureCount})`}</span>}
        </div>
        <div>
          <Icon icon={isOpen ? 'chevronUp' : 'chevronDown'} />
        </div>
      </div>

      {isOpen && <div className="px-1 pb-1">{children}</div>}
    </div>
  );
}
